import { getPermissions, hasPermission } from "../../../../utilis/permissionStorage";
import React, { useCallback, useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { FaBan, FaMoneyBillWave, FaTrashAlt } from "react-icons/fa";
import moment from "moment";
import Swal from "sweetalert2";
import {
  getInvoiceStatusLabel,
  normalizeInvoiceStatus,
} from "../../../../utilis/invoiceStatusHelpers";
import {
  resolveFinanceInvoiceEffectiveStatus,
  resolveInvoiceLatestPaymentDate,
} from "../../../../utilis/invoiceEditHelpers";
import {
  formatInvoiceMoney,
  resolveInvoiceRecordPayableTotals,
} from "../../../../utilis/invoiceLineItemHelpers";
import { canAccessAwaitingApproval } from "../../../../utilis/invoicePermissionHelpers";
import { canQuickPayInvoice } from "../../../../utilis/invoiceListPaymentHelpers";
import FinanceInvoiceQuickPayment from "../../../Finance/FinanceInvoiceQuickPayment";
import CommissionInvoiceExpectedPaymentCell from "./CommissionInvoiceExpectedPaymentCell";
import CommissionInvoiceExpectedPaymentEditor from "./CommissionInvoiceExpectedPaymentEditor";
import {
  deleteCommissionInvoice,
  listCommissionInvoices,
  voidCommissionInvoice,
} from "../commissionsApi";
import {
  mapOverdueToAwaitingPayment,
  resolveOverdueDisplay,
} from "../../../../utilis/invoiceOverdueHelpers";
import { mergeExpectedPaymentPatch } from "../../../../utilis/invoiceCreateHelpers";
import { view_ApiManager } from "../../../ApiManager/ApiManager";
import InvoiceFollowupModal from "../../../GenericComponents/InvoiceFollowupModal";
import InvoiceFollowupListBell from "../../../GenericComponents/InvoiceFollowupListBell";
import { fetchInvoiceFollowupSummariesByInvoices } from "../../../../utilis/invoiceFollowupApi";
import {
  CONTEXT_INVOICE_COMMISSION,
  hasInvoiceFollowupPermission,
} from "../../../../utilis/followupConstants";
import "../../../Clients/clients.css";

function readInvoiceFollowupPermissions() {
  try {
    return JSON.parse(JSON.stringify(getPermissions()) || "{}") || {};
  } catch {
    return {};
  }
}

const STATUS_TABS = [
  { id: "all", label: "All" },
  { id: "draft", label: "Draft" },
  { id: "awaiting_approval", label: "Awaiting Approval" },
  { id: "awaiting_payment", label: "Awaiting Payment" },
  { id: "partial", label: "Partial" },
  { id: "paid", label: "Paid" },
  { id: "void", label: "Void" },
];

function toTableInvoice(row, fallbackCurrency = "AUD") {
  const record = row?.raw || {};
  const payable = resolveInvoiceRecordPayableTotals({
    ...record,
    items: record.items ?? row.lineItems,
    line_items: record.line_items ?? row.lineItems,
    totals: record.totals ?? row.totals,
    total_amount: row.amount ?? record.total_amount ?? record.amount,
    paid_amount: row.paidAmount ?? record.paid_amount,
    amount_due: row.amountDue ?? record.amount_due,
  });

  return {
    id: row.id || record.id || null,
    invoice_number: row.invoiceNumber || record.invoice_number || "—",
    status: row.status || record.status || "draft",
    payment_status: record.payment_status || row.status || "draft",
    total_amount: payable.total_amount,
    amount_due: payable.amount_due,
    paid_amount: payable.paid_amount,
    discount_total: Number(
      row.discountTotal ??
        record.discount_total ??
        record.discountTotal ??
        0,
    ),
    currency: row.currency || record.currency || fallbackCurrency,
    issue_date:
      row.invoiceDate || record.issue_date || record.invoice_date || null,
    due_date: row.dueDate || record.due_date || null,
    expectedPaymentDate:
      row.expectedPaymentDate ||
      record.expected_payment_date ||
      null,
    expectedPaymentNote:
      row.expectedPaymentNote ||
      record.expected_payment_note ||
      "",
    reference: row.reference || row.remarks || record.reference || "",
    finance_invoice_id: row.financeInvoiceId || record.id || null,
    client_id: row.clientId || record.client_id || null,
    client_name:
      row.clientName ||
      record.client_name ||
      record.student_name ||
      row.student_name ||
      record.client_snapshot?.full_name ||
      record.client_snapshot?.name ||
      "",
    provider_name:
      row.providerName ||
      record.provider_name ||
      record.provider_snapshot?.name ||
      "",
    type_badge: row.typeBadge || null,
    invoice_type: row.invoiceType || record.invoice_type || null,
    linked_installments: row.linkedInstallments || [],
    linked_bonus_installments: row.linkedBonusInstallments || [],
    notes: row.notes || record.notes || [],
    voided_from_status:
      row.voidedFromStatus ||
      record.voided_from_status ||
      null,
    payment_date: resolveInvoiceLatestPaymentDate({
      ...record,
      ...row,
      transactions:
        record.transactions ??
        row.transactions ??
        record.finance_invoice_transactions,
    }),
    _commissionInvoiceId: row.id,
    _sourceRow: row,
  };
}

function resolveInvoiceId(invoice) {
  return invoice?.id || invoice?._commissionInvoiceId || null;
}

function resolveInvoiceNumber(invoice) {
  return invoice?.invoice_number || invoice?.invoiceNumber || "—";
}

function resolvePanelClientName(invoice, invoiceContext) {
  return (
    invoice?.client_name ||
    invoice?.raw?.student_name ||
    invoice?.raw?.client_snapshot?.full_name ||
    invoice?.raw?.client_snapshot?.name ||
    invoiceContext?.studentName ||
    "—"
  );
}

function resolvePanelProviderName(invoice, invoiceContext) {
  return (
    invoice?.provider_name ||
    invoice?.raw?.provider_name ||
    invoiceContext?.providerName ||
    invoiceContext?.provider?.name ||
    "—"
  );
}

function mapCommissionInvoiceTabStatus(status) {
  return mapOverdueToAwaitingPayment(status);
}

function resolveInvoiceStatus(invoice) {
  const notes = invoice?.notes || [];
  if (
    notes.some(
      (note) => String(note?.action || "").toLowerCase() === "voided",
    )
  ) {
    return "void";
  }

  let explicit = normalizeInvoiceStatus(
    invoice?.status || invoice?.payment_status || "",
  );

  // Commission API may return "pending" before finance-style statuses.
  if (!explicit || explicit === "pending") {
    explicit = "draft";
  }

  if (["void", "cancelled", "refunded"].includes(explicit)) {
    return explicit;
  }

  let status = mapCommissionInvoiceTabStatus(
    resolveFinanceInvoiceEffectiveStatus(invoice, { mapOverdueTab: true }),
  );

  if (
    invoice?.approved === true &&
    status !== "paid" &&
    status !== "partial" &&
    status !== "draft" &&
    status !== "awaiting_approval"
  ) {
    return mapCommissionInvoiceTabStatus("awaiting_payment");
  }

  return status;
}

function matchesStatusTab(status, tabId) {
  if (tabId === "all") return true;
  return status === tabId;
}

function formatMoney(value, currency = "AUD") {
  return formatInvoiceMoney(value, currency, {});
}

function pickDefaultTab(invoices) {
  for (const tab of STATUS_TABS) {
    if (tab.id === "all") continue;
    const hasRows = invoices.some((invoice) =>
      matchesStatusTab(resolveInvoiceStatus(invoice), tab.id),
    );
    if (hasRows) return tab.id;
  }
  return "draft";
}

function CommissionInvoicesPanel({
  planId = null,
  clientId = null,
  returnTo = "",
  currency = "AUD",
  canCreateInvoice = false,
  onCreateInvoice,
  onChanged,
  onInvoicesLoaded,
  refreshKey = 0,
  initialStatusTab = null,
  invoiceContext = null,
}) {
  const [loading, setLoading] = useState(false);
  const [invoices, setInvoices] = useState([]);
  const [activeTab, setActiveTab] = useState("draft");
  const [error, setError] = useState(null);
  const [deletingId, setDeletingId] = useState(null);
  const [voidingId, setVoidingId] = useState(null);
  const [paymentTarget, setPaymentTarget] = useState(null);
  const [editingExpectedPaymentId, setEditingExpectedPaymentId] = useState(null);
  const [followupTarget, setFollowupTarget] = useState(null);
  const [followupUsers, setFollowupUsers] = useState([]);
  const [followupSummaries, setFollowupSummaries] = useState({});
  const [followupSummaryRefreshKey, setFollowupSummaryRefreshKey] = useState(0);

  const invoiceFollowupPermissions = useMemo(
    () => readInvoiceFollowupPermissions(),
    [],
  );
  const canAddInvoiceFollowup = hasInvoiceFollowupPermission(
    CONTEXT_INVOICE_COMMISSION,
    invoiceFollowupPermissions,
  );

  const showExpectedPaymentColumns =
    activeTab === "awaiting_payment" || activeTab === "partial";
  const showPaymentDateColumn =
    activeTab === "partial" || activeTab === "paid";

  const tableColumnCount =
    11 + (showExpectedPaymentColumns ? 2 : 0) + (showPaymentDateColumn ? 1 : 0);

  useEffect(() => {
    view_ApiManager(
      "getAllUsers",
      0,
      0,
      (res) => setFollowupUsers(res?.data?.data || []),
      () => setFollowupUsers([]),
    );
  }, []);

  useEffect(() => {
    if (!canAddInvoiceFollowup || !invoices.length) {
      setFollowupSummaries({});
      return undefined;
    }

    const invoiceIds = invoices
      .map((invoice) => resolveInvoiceId(invoice))
      .filter((id) => id != null && String(id).trim());

    if (!invoiceIds.length) {
      setFollowupSummaries({});
      return undefined;
    }

    let cancelled = false;
    (async () => {
      const summaries = await fetchInvoiceFollowupSummariesByInvoices(invoiceIds);
      if (cancelled) return;
      setFollowupSummaries(summaries);
    })();

    return () => {
      cancelled = true;
    };
  }, [invoices, followupSummaryRefreshKey, canAddInvoiceFollowup]);

  const loadInvoices = useCallback(async () => {
    if (!planId) {
      setInvoices([]);
      setError(null);
      return;
    }

    setLoading(true);
    setError(null);
    try {
      // GET /new-commissions/{planId}/invoices
      const rows = await listCommissionInvoices(planId);
      const mapped = (rows || []).map((row) => toTableInvoice(row, currency));
      setInvoices(mapped);
      setActiveTab((prev) => {
        const preferredTab =
          initialStatusTab &&
          STATUS_TABS.some((tab) => tab.id === initialStatusTab)
            ? initialStatusTab
            : null;
        if (
          preferredTab &&
          mapped.some((invoice) =>
            matchesStatusTab(resolveInvoiceStatus(invoice), preferredTab),
          )
        ) {
          return preferredTab;
        }

        const stillValid = mapped.some((invoice) =>
          matchesStatusTab(resolveInvoiceStatus(invoice), prev),
        );
        return stillValid ? prev : pickDefaultTab(mapped);
      });
      if (typeof onInvoicesLoaded === "function") {
        onInvoicesLoaded(rows || []);
      }
    } catch (err) {
      console.error(err);
      setError(err?.message || "Failed to load commission invoices.");
      setInvoices([]);
    } finally {
      setLoading(false);
    }
  }, [planId, currency, onInvoicesLoaded, initialStatusTab]);

  useEffect(() => {
    loadInvoices();
  }, [loadInvoices, refreshKey]);

  useEffect(() => {
    if (
      initialStatusTab &&
      STATUS_TABS.some((tab) => tab.id === initialStatusTab)
    ) {
      setActiveTab(initialStatusTab);
    }
  }, [initialStatusTab, refreshKey]);

  useEffect(() => {
    setEditingExpectedPaymentId(null);
  }, [activeTab]);

  const buildEditPath = (invoice, invoiceId) => {
    const resolvedClientId =
      clientId || invoice?.client_id || invoice?.clientId || "";
    const base = `/commissions/edit-invoice/${resolvedClientId}/${invoiceId}`;
    if (!returnTo) return base;
    return `${base}?returnTo=${encodeURIComponent(returnTo)}`;
  };

  const tabCounts = useMemo(() => {
    const counts = {};
    STATUS_TABS.forEach((tab) => {
      counts[tab.id] = invoices.filter((invoice) =>
        matchesStatusTab(resolveInvoiceStatus(invoice), tab.id),
      ).length;
    });
    return counts;
  }, [invoices]);

  const filteredInvoices = useMemo(
    () =>
      invoices.filter((invoice) =>
        matchesStatusTab(resolveInvoiceStatus(invoice), activeTab),
      ),
    [invoices, activeTab],
  );

  const summaryCurrency =
    filteredInvoices[0]?.currency || invoices[0]?.currency || currency;
  const summaryTotal = filteredInvoices.reduce(
    (sum, invoice) =>
      sum + Number(invoice?.amount_due ?? invoice?.total_amount ?? 0),
    0,
  );

  const handleExpectedPaymentSaved = useCallback((invoiceId, patch = {}) => {
    setInvoices((prev) =>
      prev.map((row) => {
        if (String(resolveInvoiceId(row)) !== String(invoiceId)) return row;
        return mergeExpectedPaymentPatch(row, patch);
      }),
    );
    setEditingExpectedPaymentId(null);
  }, []);

  const canClickAwaitingApprovalTab = canAccessAwaitingApproval();

  const handleDeleteInvoice = async (invoice) => {
    const invoiceId = resolveInvoiceId(invoice);
    if (!invoiceId) return;

    const confirmed = await Swal.fire({
      title: "Delete invoice?",
      text: `Delete invoice ${resolveInvoiceNumber(invoice)}? This cannot be undone.`,
      icon: "warning",
      showCancelButton: true,
      confirmButtonText: "Delete",
      cancelButtonText: "Cancel",
      confirmButtonColor: "#dc2626",
    });

    if (!confirmed.isConfirmed) return;

    try {
      setDeletingId(String(invoiceId));
      await deleteCommissionInvoice(invoiceId);

      setInvoices((prev) =>
        prev.filter(
          (row) => String(resolveInvoiceId(row)) !== String(invoiceId),
        ),
      );

      await Swal.fire({
        icon: "success",
        title: "Deleted",
        text: "Commission invoice deleted successfully.",
        timer: 1400,
        showConfirmButton: false,
      });

      await loadInvoices();
      if (typeof onChanged === "function") onChanged();
    } catch (err) {
      Swal.fire(
        "Error",
        err?.message || "Failed to delete commission invoice.",
        "error",
      );
    } finally {
      setDeletingId(null);
    }
  };

  const handleVoidInvoice = async (invoice) => {
    const invoiceId = resolveInvoiceId(invoice);
    if (!invoiceId) return;

    const confirmed = await Swal.fire({
      title: "Void invoice?",
      text: `Move invoice ${resolveInvoiceNumber(invoice)} to Void? The record will be kept for reference.`,
      icon: "warning",
      showCancelButton: true,
      confirmButtonText: "Void",
      cancelButtonText: "Cancel",
      confirmButtonColor: "#64748b",
    });

    if (!confirmed.isConfirmed) return;

    try {
      setVoidingId(String(invoiceId));
      await voidCommissionInvoice(invoiceId);

      await Swal.fire({
        icon: "success",
        title: "Voided",
        text: "Invoice moved to Void.",
        timer: 1400,
        showConfirmButton: false,
      });

      await loadInvoices();
      setActiveTab("void");
      if (typeof onChanged === "function") onChanged();
    } catch (err) {
      Swal.fire(
        "Error",
        err?.message || "Failed to void commission invoice.",
        "error",
      );
    } finally {
      setVoidingId(null);
    }
  };

  return (
    <div className="client-invoices-xero client-invoices-xero--embedded">
      <div className="client-invoices-xero__page-head client-invoices-xero__page-head--split">
        <div>
          <h2 className="client-invoices-xero__title">Invoices</h2>
        </div>
        <div className="client-invoices-xero__actions client-invoices-xero__actions--right">
          <button
            type="button"
            onClick={onCreateInvoice}
            disabled={!canCreateInvoice || !planId}
            className="client-invoices-xero__btn client-invoices-xero__btn--primary"
            title={
              canCreateInvoice
                ? "Create invoice for installments, bonus, or application/material fees"
                : "Add installments or save application/material fees first"
            }
          >
            New invoice
          </button>
        </div>
      </div>

      {!planId ? (
        <div className="client-invoices-xero__empty px-3">
          <p className="mb-0">Save a commission plan to load invoices.</p>
        </div>
      ) : (
        <>
          <div
            className="client-invoices-xero__tabs"
            role="tablist"
            aria-label="Invoice status"
          >
            {STATUS_TABS.map((tab) => {
              const isActive = activeTab === tab.id;
              const count = tabCounts[tab.id] ?? 0;
              const isAwaitingApprovalTab = tab.id === "awaiting_approval";
              const isTabDisabled =
                isAwaitingApprovalTab && !canClickAwaitingApprovalTab;

              return (
                <button
                  key={tab.id}
                  type="button"
                  role="tab"
                  aria-selected={isActive}
                  aria-disabled={isTabDisabled}
                  disabled={isTabDisabled}
                  className={`client-invoices-xero__tab${
                    isActive ? " is-active" : ""
                  }${isTabDisabled ? " is-disabled" : ""}`}
                  onClick={() => {
                    if (isTabDisabled) return;
                    setActiveTab(tab.id);
                  }}
                >
                  {tab.label}
                  {` (${count})`}
                </button>
              );
            })}
          </div>

          <div className="client-invoices-xero__toolbar cms-commission-invoices-panel__toolbar">
            <div className="client-invoices-xero__toolbar-right">
              <span className="client-invoices-xero__summary">
                {filteredInvoices.length} item
                {filteredInvoices.length === 1 ? "" : "s"} |{" "}
                {formatMoney(summaryTotal, summaryCurrency)}
              </span>
            </div>
          </div>

          <div className="client-invoices-xero__table-card">
            {loading ? (
              <div className="client-invoices-xero__loading">
                Loading invoices...
              </div>
            ) : error ? (
              <div className="client-invoices-xero__empty">
                <p className="mb-0 text-danger">{error}</p>
              </div>
            ) : filteredInvoices.length === 0 ? (
              <div className="client-invoices-xero__empty">
                <p className="mb-0">No invoices found for this status.</p>
              </div>
            ) : (
              <div className="cms-commissions-table-wrap table-responsive cms-commission-invoices-table-wrap">
                <table className="client-invoices-xero__table cms-commission-invoices-table">
                  <thead>
                    <tr>
                      <th>#</th>
                      <th className="cms-commission-invoices-table__client">Client</th>
                      <th className="cms-commission-invoices-table__to">To</th>
                      <th>Date</th>
                      <th>Due date</th>
                      {showExpectedPaymentColumns ? (
                        <th className="cms-commission-invoices-table__expected">
                          Expected date
                        </th>
                      ) : null}
                      <th className="text-end">Amount</th>
                      <th className="text-end">Discount</th>
                      <th className="text-end">Paid</th>
                      {showPaymentDateColumn ? <th>Payment date</th> : null}
                      <th className="text-end">Remaining</th>
                      {showExpectedPaymentColumns ? (
                        <th className="cms-commission-invoices-table__overdue">
                          Overdue
                        </th>
                      ) : null}
                      <th>Status</th>
                      <th className="client-invoices-xero__actions-col text-end">
                        Actions
                      </th>
                    </tr>
                  </thead>
                  <tbody>
                    {filteredInvoices.map((invoice, index) => {
                      const invoiceId = resolveInvoiceId(invoice);
                      const status = resolveInvoiceStatus(invoice);
                      const rowCurrency = invoice?.currency || summaryCurrency;
                      const amount = Number(invoice?.total_amount) || 0;
                      const discount = Number(invoice?.discount_total) || 0;
                      const paidAmount = Number(invoice?.paid_amount) || 0;
                      const remainingAmount = Number(invoice?.amount_due) || 0;
                      const issueDate = invoice?.issue_date;
                      const dueDate = invoice?.due_date;
                      const paymentDate =
                        invoice?.payment_date ||
                        resolveInvoiceLatestPaymentDate({
                          ...invoice,
                          ...(invoice?._sourceRow?.raw || {}),
                          transactions:
                            invoice?._sourceRow?.raw?.transactions ??
                            invoice?._sourceRow?.transactions,
                        });
                      const isDeleting = deletingId === String(invoiceId);
                      const isVoiding = voidingId === String(invoiceId);
                      const isRowBusy = isDeleting || isVoiding;
                      const canVoid = activeTab === "awaiting_payment";
                      const canPay = canQuickPayInvoice(
                        invoice,
                        resolveInvoiceStatus,
                      );
                      const linkedLineCount =
                        (invoice?.linked_installments?.length || 0) +
                        (invoice?.linked_bonus_installments?.length || 0);

                      const isEditingExpected =
                        editingExpectedPaymentId != null &&
                        String(editingExpectedPaymentId) === String(invoiceId);
                      const overdueDisplay = resolveOverdueDisplay(invoice);
                      const rowClientName = resolvePanelClientName(
                        invoice,
                        invoiceContext,
                      );
                      const rowProviderName = resolvePanelProviderName(
                        invoice,
                        invoiceContext,
                      );

                      return (
                        <React.Fragment key={invoiceId || `invoice-${index}`}>
                        <tr>
                          <td>
                            {invoiceId &&
                            (clientId || invoice?.client_id) ? (
                              <Link
                                className="client-invoices-xero__invoice-link"
                                to={buildEditPath(invoice, invoiceId)}
                                state={{
                                  returnTo,
                                  planId,
                                  invoiceStatus: status,
                                  commissionInstallmentIds:
                                    invoice?.linked_installments?.map(
                                      (row) => row?.id,
                                    ) || [],
                                  bonusInstallmentIds:
                                    invoice?.linked_bonus_installments?.map(
                                      (row) => row?.id,
                                    ) || [],
                                  ...(invoiceContext || {}),
                                }}
                                style={{ color: "#007BFF", fontWeight: 600 }}
                              >
                                {resolveInvoiceNumber(invoice)}
                              </Link>
                            ) : (
                              <span className="client-invoices-xero__invoice-link">
                                {resolveInvoiceNumber(invoice)}
                              </span>
                            )}
                          </td>
                          <td
                            className="cms-commission-invoices-table__contact-cell cms-commission-invoices-table__client"
                            title={
                              rowClientName && rowClientName !== "—"
                                ? rowClientName
                                : undefined
                            }
                          >
                            <span className="cms-commission-invoices-table__contact-name">
                              {rowClientName}
                            </span>
                          </td>
                          <td
                            className="cms-commission-invoices-table__contact-cell cms-commission-invoices-table__to"
                            title={
                              rowProviderName && rowProviderName !== "—"
                                ? rowProviderName
                                : undefined
                            }
                          >
                            <span className="cms-commission-invoices-table__contact-name">
                              {rowProviderName}
                            </span>
                          </td>
                          <td>
                            {issueDate
                              ? moment(issueDate).format("MMM D, YYYY")
                              : ""}
                          </td>
                          <td>
                            {dueDate
                              ? moment(dueDate).format("MMM D, YYYY")
                              : ""}
                          </td>
                          {showExpectedPaymentColumns ? (
                            <td className="cms-commission-invoices-table__expected">
                              <CommissionInvoiceExpectedPaymentCell
                                invoice={invoice}
                                disabled={loading || isRowBusy}
                                onStartEdit={() =>
                                  setEditingExpectedPaymentId(invoiceId)
                                }
                              />
                            </td>
                          ) : null}
                          <td className="text-end fw-semibold">
                            {formatMoney(amount, rowCurrency)}
                          </td>
                          <td className="text-end client-invoices-xero__discount">
                            {discount > 0
                              ? `-${formatMoney(discount, rowCurrency)}`
                              : formatMoney(0, rowCurrency)}
                          </td>
                          <td className="text-end">
                            {formatMoney(paidAmount, rowCurrency)}
                          </td>
                          {showPaymentDateColumn ? (
                            <td>
                              {paymentDate
                                ? moment(paymentDate).format("MMM D, YYYY")
                                : "—"}
                            </td>
                          ) : null}
                          <td className="text-end">
                            {formatMoney(remainingAmount, rowCurrency)}
                          </td>
                          {showExpectedPaymentColumns ? (
                            <td className="cms-commission-invoices-table__overdue">
                              {overdueDisplay.isOverdue ? (
                                <span
                                  className="cms-commission-invoices-table__overdue-badge"
                                  title={`Past due by ${overdueDisplay.label}`}
                                >
                                  {overdueDisplay.label}
                                </span>
                              ) : (
                                <span className="cms-commission-invoices-table__overdue-none">
                                  —
                                </span>
                              )}
                            </td>
                          ) : null}
                          <td>
                            <span className="client-invoices-xero__status">
                              {getInvoiceStatusLabel(resolveInvoiceStatus(invoice))}
                            </span>
                          </td>
                          <td className="text-end">
                            <div className="client-invoices-xero__row-actions">
                              {canPay ? (
                                <button
                                  type="button"
                                  className="client-invoices-xero__icon-action client-invoices-xero__icon-action--payment"
                                  title="Add payment"
                                  aria-label={`Add payment for invoice ${resolveInvoiceNumber(
                                    invoice,
                                  )}`}
                                  disabled={!invoiceId}
                                  onClick={() =>
                                    setPaymentTarget({
                                      invoiceId,
                                      clientId:
                                        clientId || invoice?.client_id || "",
                                      invoiceNumber:
                                        resolveInvoiceNumber(invoice),
                                      isCommission: true,
                                      multiLineHint: linkedLineCount > 1,
                                      initialAmountDue: remainingAmount,
                                    })
                                  }
                                >
                                  <FaMoneyBillWave aria-hidden="true" />
                                </button>
                              ) : null}
                              {canVoid && activeTab !== "void" ? (
                                <button
                                  type="button"
                                  className="client-invoices-xero__icon-action"
                                  title="Void invoice"
                                  aria-label={`Void invoice ${resolveInvoiceNumber(
                                    invoice,
                                  )}`}
                                  disabled={isRowBusy || !invoiceId}
                                  onClick={() => handleVoidInvoice(invoice)}
                                  style={{
                                    color: "#64748b",
                                    borderColor: "#cbd5e1",
                                  }}
                                >
                                  <FaBan aria-hidden="true" />
                                </button>
                              ) : null}
                              {canAddInvoiceFollowup && invoiceId ? (
                                <InvoiceFollowupListBell
                                  count={
                                    followupSummaries[String(invoiceId)]?.total ||
                                    0
                                  }
                                  summary={
                                    followupSummaries[String(invoiceId)] || null
                                  }
                                  disabled={isRowBusy}
                                  ariaLabel={`Follow-ups for invoice ${resolveInvoiceNumber(
                                    invoice,
                                  )}`}
                                  onClick={() =>
                                    setFollowupTarget({
                                      financeInvoiceId: Number(invoiceId),
                                      contextType: CONTEXT_INVOICE_COMMISSION,
                                      clientId: clientId
                                        ? Number(clientId)
                                        : null,
                                      providerId: invoiceContext?.providerId
                                        ? Number(invoiceContext.providerId)
                                        : null,
                                      commissionPlanId: planId
                                        ? Number(planId)
                                        : null,
                                      applicationId: invoiceContext?.applicationId
                                        ? Number(invoiceContext.applicationId)
                                        : null,
                                      applicationCourseId:
                                        invoiceContext?.applicationCourseId
                                          ? Number(
                                              invoiceContext.applicationCourseId,
                                            )
                                          : null,
                                    })
                                  }
                                />
                              ) : null}
                              <button
                                type="button"
                                className="client-invoices-xero__icon-action client-invoices-xero__icon-action--danger"
                                title="Delete invoice"
                                aria-label={`Delete invoice ${resolveInvoiceNumber(
                                  invoice,
                                )}`}
                                disabled={isRowBusy || !invoiceId}
                                onClick={() => handleDeleteInvoice(invoice)}
                                style={{
                                  color: "#dc2626",
                                  borderColor: "#fecaca",
                                }}
                              >
                                <FaTrashAlt aria-hidden="true" />
                              </button>
                            </div>
                          </td>
                        </tr>
                        {showExpectedPaymentColumns && isEditingExpected ? (
                          <tr className="cms-invoice-expected-payment-row">
                            <td colSpan={tableColumnCount}>
                              <CommissionInvoiceExpectedPaymentEditor
                                inline
                                invoiceId={invoiceId}
                                expectedPaymentDate={invoice?.expectedPaymentDate}
                                expectedPaymentNote={invoice?.expectedPaymentNote}
                                onCancel={() => setEditingExpectedPaymentId(null)}
                                onSaved={(patch) =>
                                  handleExpectedPaymentSaved(invoiceId, patch)
                                }
                              />
                            </td>
                          </tr>
                        ) : null}
                        </React.Fragment>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        </>
      )}

      <FinanceInvoiceQuickPayment
        show={Boolean(paymentTarget)}
        invoiceId={paymentTarget?.invoiceId || null}
        clientId={paymentTarget?.clientId || null}
        invoiceNumber={paymentTarget?.invoiceNumber || ""}
        isCommission={Boolean(paymentTarget?.isCommission)}
        multiLineHint={Boolean(paymentTarget?.multiLineHint)}
        initialAmountDue={paymentTarget?.initialAmountDue}
        onClose={() => setPaymentTarget(null)}
        onSuccess={async () => {
          await loadInvoices();
          if (typeof onChanged === "function") onChanged();
        }}
      />

      <InvoiceFollowupModal
        show={Boolean(followupTarget)}
        contextType={
          followupTarget?.contextType || CONTEXT_INVOICE_COMMISSION
        }
        financeInvoiceId={followupTarget?.financeInvoiceId ?? null}
        commissionPlanId={followupTarget?.commissionPlanId ?? null}
        clientId={followupTarget?.clientId ?? null}
        providerId={followupTarget?.providerId ?? null}
        applicationId={followupTarget?.applicationId ?? null}
        applicationCourseId={followupTarget?.applicationCourseId ?? null}
        users={followupUsers}
        onClose={() => setFollowupTarget(null)}
        onSuccess={async () => {
          setFollowupTarget(null);
          setFollowupSummaryRefreshKey((key) => key + 1);
          await loadInvoices();
          if (typeof onChanged === "function") onChanged();
        }}
      />
    </div>
  );
}

export default CommissionInvoicesPanel;
